import moment from 'moment'
import { dollarFormatter } from './money'

const parseCurrencyDollars = (value) => {
  const numeric = Number(String(value ?? '').replace(/[^0-9.-]/g, ''))
  return Number.isFinite(numeric) ? numeric : 0
}

const getDateInputFromDeliveryDecision = (decision = {}) => {
  if (decision?.status === 'manual-date' && decision?.dateInput) return decision.dateInput
  if (!['scheduled', 'available-now'].includes(decision?.status) || !decision?.label) return ''
  const parsed = moment(decision.label, ['MM/DD/YYYY', 'M/D/YYYY', 'M/D/YY', 'YYYY-MM-DD'], true)
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : ''
}

export const parseAddressForShipTo = (address = '') => {
  const parts = String(address || '').split(',').map((part) => part.trim()).filter(Boolean)
  const street = parts[0] || ''
  const city = parts[1] || ''
  const stateZip = parts.slice(2).join(' ')
  const stateZipMatch = stateZip.match(/\b([A-Z]{2})\b\s*(\d{5}(?:-\d{4})?)?/i)

  return {
    name: 'Pasted quote address',
    shippingAddress: street,
    shippingCity: city,
    shippingState: stateZipMatch?.[1]?.toUpperCase() || '',
    shippingZip: stateZipMatch?.[2] || '',
  }
}

export const buildQuoteSourceNotes = ({
  fields = {},
  parsed = {},
  missingModels = [],
  totals = {},
  walkUpFloor = '',
  deliveryDecision = {},
  availabilityProjections = [],
  quoteNumber = '',
} = {}) => {
  const checkedAvailabilityProjections = (availabilityProjections || []).filter((projection) => projection.status !== 'not-checked')
  const walkUpBreakdown = Array.isArray(totals?.walkUpBreakdown) ? totals.walkUpBreakdown : []
  const walkUpBreakdownText = walkUpBreakdown.length
    ? walkUpBreakdown
        .map((row) => `${row.displayFloorLabel || row.floorLabel || 'Needs floor'}: ${row.quantity || 0} unit(s)${row.total ? ` = ${dollarFormatter.format(row.total)}` : ''}`)
        .join('; ')
    : ''
  const noteLines = [
    'Quote generated from quote builder.',
    quoteNumber ? `Quote number: ${quoteNumber}` : null,
    fields.address ? `Ship to address: ${fields.address}` : null,
    fields.dateAcquired ? `Quote date: ${moment(fields.dateAcquired).format('MM/DD/YYYY')}` : null,
    fields.dateRequired ? `Date required: ${fields.dateRequired}` : null,
    fields.reasonForPurchase ? `Reason for purchase: ${fields.reasonForPurchase}` : null,
    fields.deliveryNote ? `Delivery note: ${fields.deliveryNote}` : null,
    fields.walkUp ? `Walk up/Elevator: ${fields.walkUp}` : null,
    walkUpFloor ? `Walk up floor: ${walkUpFloor}` : null,
    totals?.walkUpTotal ? `Walk up charge: ${walkUpBreakdownText || `${dollarFormatter.format(totals.walkUpRate)} x ${totals.itemCount}`} = ${dollarFormatter.format(totals.walkUpTotal)}` : null,
    deliveryDecision?.label && !['No models yet', 'Run inventory lookup'].includes(deliveryDecision.label)
      ? `Earliest delivery based on inventory: ${deliveryDecision.label}`
      : null,
    checkedAvailabilityProjections.length
      ? `Inventory proof: ${checkedAvailabilityProjections.map((projection) => `${projection.modelNumber} requested ${projection.requestedQuantity}, target ${projection.targetQuantity}, available ${projection.currentAvailable ?? 'N/A'}, earliest ${projection.earliestLabel}`).join('; ')}`
      : null,
    parsed.subtotal ? `Source subtotal: ${dollarFormatter.format(parsed.subtotal)}` : null,
    missingModels.length ? `Products not found in catalog: ${missingModels.join(', ')}` : null,
  ]

  return noteLines.filter(Boolean).join('\n')
}

export const buildInvoiceDraftFromQuote = (quote = {}) => {
  const fields = quote.fields || {}
  const totals = quote.totals || {}
  const savedHaulAwayRate = fields.haulAwayFee !== undefined && fields.haulAwayFee !== null && fields.haulAwayFee !== ''
    ? fields.haulAwayFee
    : totals.haulAwayFee
  const haulAwayFee = Math.max(0, parseCurrencyDollars(savedHaulAwayRate))
  const haulAwayQuantity = (quote.items || []).reduce((sum, item) => sum + Math.max(0, Number(item.quantity) || 0), 0)
  const deliveryFee = Number(totals.deliveryFee) || parseCurrencyDollars(fields.deliveryFee)
  const receivedByDateInput = fields.earliestFreeDeliveryDate || getDateInputFromDeliveryDecision(quote.deliveryDecision)
  const receivedByDate = receivedByDateInput ? moment(receivedByDateInput).toISOString() : undefined

  return {
    quoteId: quote._id,
    quoteNumber: quote.quoteNumber,
    source: 'saved-quote',
    type: 'Quotation',
    status: 'Unpaid',
    haulAway: haulAwayFee > 0 && haulAwayQuantity > 0,
    haulAwayFee,
    haulAwayCost: haulAwayFee,
    haulAwayQuantity: haulAwayQuantity || 1,
    receivedByDate,
    deliveryFee,
    deliveryFeeOverride: deliveryFee > 0,
    items: (quote.items || []).map((line) => ({
      modelNumber: line.modelNumber,
      quantity: Number(line.quantity) || 1,
      unitPrice: Number(line.unitPrice) || 0,
      unitCost: line.unitCost || '',
      discount: '',
      amount: '',
      doNotOrder: false,
      lockup: false,
    })),
  }
}
